import { CommonModule, DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, Component, Inject, Input, NgModule, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { RouterModule } from '@angular/router';
import { CUSTOMERENVIRONMENT } from '@app/common/app-tokens';
import { NewsModePersonalNews } from '@app/common/news-mode';
import { ActionLinkModule } from '@app/components/actions/action-link/action-link.component';
import { ActionRouterLinkModule } from '@app/components/actions/action-router-link/action-router-link.component';
import { ErrorTemplatesModule } from '@app/components/error-templates/error-templates.component';
import { NewsCardListModule } from '@app/components/news-card-list/news-card-list.component';
import { TextPlaceholderModule } from '@app/components/text-placeholder/text-placeholder.component';
import { WidgetPlaceholderModule } from '@app/components/widget-placeholder/widget-placeholder.component';
import { NewsPage } from '@app/models/news-page';
import { User } from '@app/models/user';
import { UserLanguageTerm } from '@app/models/user-language-term';
import { LinkModule } from '@app/directives/link.directive';
import { FeatureSettingsStoreService } from '@app/services/feature-settings-store.service';
import { LicenseService } from '@app/services/license.service';
import { MyFollowedSitesService } from '@app/services/my-followed-sites.service';
import { NewsService } from '@app/services/news.service';
import { SiteService } from '@app/services/site.service';
import { UserChannelTermsService } from '@app/services/user-channel-terms.service';
import { UserLanguageTermService } from '@app/services/user-language-term.service';
import { UserService } from '@app/services/user.service';
import { Environment } from '@environments/interface.environment';
import { combineLatest, Observable, of, race, Subject } from 'rxjs';
import { catchError, map, mergeMap, startWith, switchMap } from 'rxjs/operators';

@Component({
  selector: 'app-my-news',
  templateUrl: './my-news.component.html',
  styleUrls: ['./my-news.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MyNewsComponent implements OnInit {

  @Input() amountOfDaysBackInTime = 365;
  @Input() amountOfNewsToDisplay = 6;
  @Input() amountOfFollowedSitesToGet = 100; // max is 100, NOTE: we need to add paginated api calls if we should be able to fetch more
  @Input() preambleTextMaxLength = 140;
  @Input() maxNumberOfShowMore = 2; // How may times can you press the show more button

  fetchedAmountOfNews = 0;
  followedSiteNewsBatch = 50; // set to 50 because it gets very slow otherwise.
  amountOfNewsToIncreaseWith = this.amountOfNewsToDisplay;
  newsCenterUrl: string;
  amountOfNewsToDisplayCurrently = this.amountOfNewsToDisplay;
  personalNewsItems$: Observable<Array<NewsPage>>;
  personalNewsFromSharePoint$: Observable<Array<NewsPage>>;
  private errorSubject$ = new Subject();
  loading = false;
  featureSettings = this.featureStore.state;
  private moreNewsSubject = new Subject<number>();
  numberOfShowMoreClicks = 0;
  componentName =  'Your news';

  constructor(@Inject(CUSTOMERENVIRONMENT) private environment: Environment,
              private service: NewsService,
              private featureStore: FeatureSettingsStoreService,
              private followedSitesService: MyFollowedSitesService,
              private siteService: SiteService,
              private userService: UserService,
              private licenseService: LicenseService,
              private userChannelTermsService: UserChannelTermsService,
              private userLanguageTermsService: UserLanguageTermService,
              @Inject(DOCUMENT) private document: Document) { }

  ngOnInit() {

    this.newsCenterUrl = `${this.featureStore.state.newsArchiveLink}`;

    const userChannelTerms$ = this.userChannelTermsService.getUserChannelTerms();

    const userLanguageTerms$ = this.userLanguageTermsService.getUserLanguageTerms();

    const user$ = this.userService.getOrCreateMe();

    const subjectsToListenOn$ = race([this.moreNewsSubject, this.service.reloadAllPersonalNews$])
      .pipe(map((amount) => {
        return Number.isInteger(amount) ? Number(amount) : this.amountOfNewsToDisplayCurrently + 1;
      }));

    this.personalNewsFromSharePoint$ = this.licenseService.hasUserAny365License()
      .pipe(
        mergeMap(isLicensed => {
          if (isLicensed) {
            return subjectsToListenOn$
              .pipe(
                startWith(this.amountOfNewsToDisplayCurrently + 1),
                mergeMap((amount) => {
                  this.loading = true;

                  const myPersonalNewsFromGraph$ = this.service.getMyModernPersonalNewsFromGraph(userChannelTerms$, userLanguageTerms$);

                  if (this.featureStore.state.newsModePersonalNews === NewsModePersonalNews.GraphFromNewsCenterSiteAndSearchFromFollowedSites) {

                    const newsfromMyFollowedSites$ = this.getNewsFromMyFollowedSites$(user$, amount, userLanguageTerms$);

                    return combineLatest([myPersonalNewsFromGraph$, newsfromMyFollowedSites$])
                      .pipe(
                        map(([personalNews, newsFromFollowedSites]) => {
                          return personalNews.concat(newsFromFollowedSites);
                        })
                      );
                  } else if (this.featureStore.state.newsModePersonalNews === NewsModePersonalNews.SearchNewsFromAnySiteWithChannelField) {
                    return this.service.getPersonalModernNewsFromAllSitesFromSearch(userChannelTerms$, userLanguageTerms$, amount);
                  } else {
                    return myPersonalNewsFromGraph$;
                  }
                }),
                map((pages) => {
                  if (this.featureStore.state.newsModePersonalNews === NewsModePersonalNews.GraphFromNewsCenterSiteAndSearchFromFollowedSites
                    ||
                    this.featureStore.state.newsModePersonalNews === NewsModePersonalNews.SearchNewsFromAnySiteWithChannelField) {
                    // only fetch site names when news mode that fetch from different sites
                    // Also fetch likes and comments from individual site page instead search
                    pages.forEach(page => {
                      if (page && (!page.SiteName || !page.SiteUrl)) {
                        page.SiteInfo$ = this.siteService.getSiteInfoFromUrl(page.Path);
                        page.PageInfo$ = this.service.getModernNewsItemInfoFromPagePath(page);
                      }
                    });
                  }
                  return pages;
                }));
          } else {
            return of([]);
          }
        })
      );

    this.personalNewsItems$ = subjectsToListenOn$
      .pipe(
        startWith(this.amountOfNewsToDisplayCurrently + 1),
        switchMap(amount => {
          // If amount has changed we don't need to wait for the old call to fetch/concat/sort etc, cancel it and switch to the new
          // If workplace light is enabled concat with workplace light news
          if (this.environment.enableWorkplaceLight) {
            return combineLatest([
              this.personalNewsFromSharePoint$,
              this.service.getPersonalNewsFromApi(userChannelTerms$, userLanguageTerms$, amount)])
              .pipe(
                map(([personalNews, newsFromApi]) => {
                  return personalNews.concat(newsFromApi);
                })
              );
          } else {
            return this.personalNewsFromSharePoint$;
          }
        }),
        map((pages) => {
          // Since pages can be concatinated from multiple sources it could be unsorted
          pages = pages.sort((a, b) => {
            return new Date(b.PublishedDate).getTime() - new Date(a.PublishedDate).getTime();
          });
          this.fetchedAmountOfNews = pages.length;
          if (this.fetchedAmountOfNews > this.amountOfNewsToDisplayCurrently) {
            pages = pages.slice(0, this.amountOfNewsToDisplayCurrently);
          }

          this.loading = false;
          return pages;
        }),
        catchError(err => {
          this.errorSubject$.next(err);
          return of(null);
        }));
  }

  private getNewsFromMyFollowedSites$(user$: Observable<User>, amount: number, userLanguageTerms$: Observable<UserLanguageTerm[]>) {
    return user$
      .pipe(
        mergeMap(user => {
          if (user && user.ShowNewsFromFollowedSites) {
            // the user has selected/deselected to show news from followed sites
            return this.followedSitesService
              .getMyFollowedSitesFromSpHome(0, this.amountOfFollowedSitesToGet)
              .pipe(
                map(followedSites => followedSites.map(s => s.OriginalUrl))
              );
          } else {
            return of([]);
          }
        }),
        mergeMap(urls => {
          const amountToFetch = this.followedSiteNewsBatch <= amount ? amount : this.followedSiteNewsBatch;
          return this.service.getModernNewsFromAllSitesExceptNewsCenterFromSearch(userLanguageTerms$, amountToFetch)
            .pipe(
              map(
                // we could filter on url directly in the getModernNewsFromAllSitesExceptNewsCenterFromSearch request, but it seems to give a 400 error if we have a lot of followed sites, so we filter manually instead
                newsFromSearch => {
                  return newsFromSearch.filter(page => urls.indexOf(page.SiteUrl) !== -1);
                }));
        }));
  }

  showMoreNews() {
    this.numberOfShowMoreClicks++;
    this.amountOfNewsToDisplayCurrently = this.amountOfNewsToDisplayCurrently + this.amountOfNewsToIncreaseWith;
    this.loading = true;
    if (this.featureStore.state.newsModePersonalNews === NewsModePersonalNews.GraphFromNewsCenterSite) {
      this.moreNewsSubject.next();
    } else {
      this.moreNewsSubject.next(this.amountOfNewsToDisplayCurrently + 1);
    }
  }

  showLessNews() {
    this.numberOfShowMoreClicks = 0;
    this.amountOfNewsToDisplayCurrently = this.amountOfNewsToDisplay;
    this.moreNewsSubject.next(this.amountOfNewsToDisplayCurrently + 1);
    this.scrollIntoFeatureView();
  }

  scrollIntoFeatureView() {
    const elementId = 'app-my-news';
    const element = this.document.getElementById(elementId);

    if (element) {
      if (!/^(?:a|select|input|button|textarea)$/i.test(element.tagName)) {
        element.scrollIntoView();
      }
      element.focus();
    }
  }

}
@NgModule({
  declarations: [MyNewsComponent],
  exports: [MyNewsComponent],
  imports: [
    CommonModule,
    NewsCardListModule,
    LinkModule,
    ActionLinkModule,
    ActionRouterLinkModule,
    MatButtonModule,
    RouterModule,
    ErrorTemplatesModule,
    TextPlaceholderModule,
    WidgetPlaceholderModule
  ]
})
export class MyNewsModule { }
